import { Input, Component } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { MAX_EMBEDDED_LINKS, IEmbeddedContent, IEmbeddedContentLink } from 'rev-portal/scheduledEvents/common/EmbeddedContentTypes';

import { IUnsubscribe } from 'rev-shared/push/IUnsubscribe';
import { PushBus } from 'rev-shared/push/PushBus.Service';
import { DialogService } from 'rev-shared/ui/dialog/Dialog.Service';
import { noop } from 'rev-shared/util';

import { EmbeddedContentService } from './EmbeddedContent.Service';


@Component({
	selector: 'vb-webcast-embedded-content-sidebar',
	templateUrl: './VbWebcastEmbeddedContentSidebar.Component.html',
	standalone: false
})
export class VbWebcastEmbeddedContentSidebarComponent {
	@Input() public embeddedContent!: IEmbeddedContent;
	@Input() public webcastId!: string;

	public readonly MAX_EMBEDDED_LINKS = MAX_EMBEDDED_LINKS;
	public readonly isDuplicateNameForForm = linkName => this.contentLinks.some((c, index) => c.name === linkName && index !== this.editingLinkIndex);

	public contentLinks: IEmbeddedContentLink[] = [];
	public newLink: IEmbeddedContentLink | null = null;
	public isAddingNewLink = false;
	public editingLinkIndex: number | null = null;

	private unsubscribePush?: IUnsubscribe;

	constructor(
		private readonly EmbeddedContentService: EmbeddedContentService,
		private readonly PushBus: PushBus,
		private readonly Translate: TranslateService,
		private readonly Dialog: DialogService
	) { }

	public ngOnInit(): void {
		this.contentLinks = this.embeddedContent.contentLinks.map(link => ({ ...link }));

		this.unsubscribePush = this.subscribePush();
	}

	public ngOnDestroy(): void {
		if (this.unsubscribePush) {
			this.unsubscribePush();
		}
	}

	private subscribePush(): IUnsubscribe {
		return this.PushBus.subscribe(this.webcastId, {

			EmbeddedContentLinkProcessed: eventData => {
				this.contentLinks = this.embeddedContent.contentLinks.map(link => ({ ...link }));
			},

			EmbeddedContentLinkUpdated: eventData => {
				this.contentLinks = this.embeddedContent.contentLinks.map(link => ({ ...link }));
			},

			EmbeddedContentLinkDeleted: eventData => {
				this.contentLinks = this.embeddedContent.contentLinks.map(link => ({ ...link }));
			}

		});
	}

	public onAddNewClick(): void {
		this.newLink = {
			name: '',
			code: '',
			icon: ''
		};
		this.editingLinkIndex = null;
		this.isAddingNewLink = true;
	}

	public onEditEntry(index: number): void {
		this.newLink = { ...this.contentLinks[index] };
		this.editingLinkIndex = index;
		this.isAddingNewLink = true;
	}

	public onCancelAddEntry(): void {
		this.newLink = null;
		this.editingLinkIndex = null;
		this.isAddingNewLink = false;
	}

	public onSaveEntry(link: IEmbeddedContentLink): void {
		if (this.editingLinkIndex !== null) {
			this.contentLinks[this.editingLinkIndex] = {
				...this.contentLinks[this.editingLinkIndex],
				...link
			};
			this.EmbeddedContentService.updateEmbeddedContentLink(this.webcastId, this.contentLinks[this.editingLinkIndex]);
		} else {
			const linkToAdd = { ...link };

			this.contentLinks.push(linkToAdd);
			this.EmbeddedContentService.addEmbeddedContentLink(this.webcastId, linkToAdd);
		}

		this.onCancelAddEntry();
	}

	public onUpdateEntry(index: number): void {
		this.EmbeddedContentService.updateEmbeddedContentLink(this.webcastId, this.contentLinks[index]);
	}

	public onDeleteEntry(index: number): void {
		if (this.contentLinks[index].id) {
			const linkId = this.contentLinks[index].id;

			this.Dialog.openConfirmationDialog(
				{
					title: this.Translate.instant('Event_Embed_Content_Delete'),
					message: this.Translate.instant('Event_Embed_Content_Delete_Msg'),
					actionText: this.Translate.instant('Ok'),
					cancelText: this.Translate.instant('Cancel'),
					themed: true
				}
			).result
				.then(() => {
					this.EmbeddedContentService.deleteEmbeddedContentLink(this.webcastId, linkId);
				})
				.catch(noop);
		} else {
			this.contentLinks = this.contentLinks.filter(c => c.id);
		}
	}

	public isAddNewDisabled(): boolean {
		return this.isAddingNewLink || this.contentLinks?.length >= this.MAX_EMBEDDED_LINKS;
	}

}
